import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  setCurrentCatcher,
  setCurrentPitcher,
  setRoster,
  setTeam,
} from "../store/slices/game";
import { setSport } from "../store/slices/session";
import type { RootState } from "../store/store";
import type { IPlayer, TTeamSides } from "../store/types";
import { GameSetupFooter } from "./GameSetupFooter";
import { PlayerSelectModal } from "./PlayerSelectModal";
import { TeamLineupPanel } from "./TeamLineupPanel";
import { TeamPanel } from "./TeamPanel";
import { type TeamSelectData, TeamSelectModal } from "./TeamSelectModal";

/**
 * GameSetup component - Split screen for team selection and lineup building
 *
 * Shows either:
 * - TeamPanel (empty state) when no team is selected
 * - TeamLineupPanel (lineup builder) when a team is selected
 *
 * Navigation back to mode selection is handled by the top navbar.
 */
const GameSetup = () => {
  const dispatch = useDispatch();

  // Team selection modal state
  const [teamSelectModal, setTeamSelectModal] = useState<{
    isOpen: boolean;
    team: TTeamSides;
  }>({
    isOpen: false,
    team: "away",
  });

  // Player selection modal state (for pitcher/catcher)
  const [playerSelectModal, setPlayerSelectModal] = useState<{
    isOpen: boolean;
    team: TTeamSides;
    type: "pitcher" | "catcher";
  }>({
    isOpen: false,
    team: "away",
    type: "pitcher",
  });

  const rosters = useSelector((state: RootState) => state.game.rosters);
  const awayTeamName = rosters.away.name;
  const homeTeamName = rosters.home.name;

  // Team selection handlers
  const handleOpenTeamSelect = (team: TTeamSides) => {
    setTeamSelectModal({ isOpen: true, team });
  };

  const handleCloseTeamSelect = () => {
    setTeamSelectModal({ isOpen: false, team: "away" });
  };

  const handleTeamSelect = (data: TeamSelectData) => {
    const { team } = teamSelectModal;

    if (data.sport) dispatch(setSport(data.sport));

    dispatch(
      setTeam({
        team,
        name: data.teamName,
        uuid: data.teamUuid,
      }),
    );

    dispatch(
      setRoster({
        team,
        roster: data.teamRoster,
      }),
    );

    handleCloseTeamSelect();
  };

  // Player selection handlers (pitcher/catcher)
  const handleOpenPlayerSelect = (
    team: TTeamSides,
    type: "pitcher" | "catcher",
  ) => {
    setPlayerSelectModal({ isOpen: true, team, type });
  };

  const handleClosePlayerSelect = () => {
    setPlayerSelectModal({ isOpen: false, team: "away", type: "pitcher" });
  };

  const handlePlayerSelect = (player: IPlayer) => {
    const { team, type } = playerSelectModal;

    if (type === "pitcher") {
      dispatch(setCurrentPitcher({ team, player }));
    } else {
      dispatch(setCurrentCatcher({ team, player }));
    }
  };

  const awayTeamSelected = awayTeamName.length > 0;
  const homeTeamSelected = homeTeamName.length > 0;

  return (
    <div className="flex h-full flex-col bg-white">
      {/* Main content - split screen */}
      <div className="flex flex-1 overflow-hidden">
        {/* Away Team Panel */}
        {awayTeamSelected ? (
          <TeamLineupPanel
            side="away"
            onEditTeam={() => handleOpenTeamSelect("away")}
            onSelectPlayer={(type) => handleOpenPlayerSelect("away", type)}
          />
        ) : (
          <TeamPanel
            side="away"
            teamName={awayTeamName}
            onSelectTeam={() => handleOpenTeamSelect("away")}
          />
        )}

        {/* Divider */}
        <div className="w-px bg-gray-200" />

        {/* Home Team Panel */}
        {homeTeamSelected ? (
          <TeamLineupPanel
            side="home"
            onEditTeam={() => handleOpenTeamSelect("home")}
            onSelectPlayer={(type) => handleOpenPlayerSelect("home", type)}
          />
        ) : (
          <TeamPanel
            side="home"
            teamName={homeTeamName}
            onSelectTeam={() => handleOpenTeamSelect("home")}
          />
        )}
      </div>

      {/* Start Game Footer */}
      <GameSetupFooter />

      {/* Team Selection Modal */}
      <TeamSelectModal
        isOpen={teamSelectModal.isOpen}
        onClose={handleCloseTeamSelect}
        onSubmit={handleTeamSelect}
        title={`Select ${teamSelectModal.team === "away" ? "Away" : "Home"} Team`}
      />

      {/* Player Selection Modal (Pitcher/Catcher) */}
      <PlayerSelectModal
        isOpen={playerSelectModal.isOpen}
        onClose={handleClosePlayerSelect}
        onSubmit={handlePlayerSelect}
        roster={rosters[playerSelectModal.team].roster}
        type={playerSelectModal.type}
      />
    </div>
  );
};

export default GameSetup;
